import { z } from 'zod'

import { Translation } from '@bemlo/i18n'

/**
 * @see https://github.com/colinhacks/zod/blob/master/src/locales/en.ts
 */
export const translateIssueMessage = (
  t: Translation['form'],
  issue: z.ZodIssueOptionalMessage,
) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_date:
      return t.invalid_date()
    case z.ZodIssueCode.invalid_string:
      if (typeof issue.validation === 'object') {
        const v = issue.validation
        if ('includes' in v) {
          return `${t.the_value()} ${t.must_contain()} "${v.includes}"`
        } else if ('startsWith' in v) {
          return `${t.the_value()} ${t.must_start_with()} "${v.startsWith}"`
        } else if ('endsWith' in v) {
          return `${t.the_value()} ${t.must_end_with()} "${v.endsWith}"`
        }
      }
      if (issue.validation === 'email') {
        return t.invalid_email()
      }
      return t.invalid_value()
    case z.ZodIssueCode.invalid_type:
      return issue.received === z.ZodParsedType.undefined
        ? t.required()
        : t.invalid_value()
    case z.ZodIssueCode.not_multiple_of:
      return t.the_value_must_be_a_multiple_of(issue.multipleOf)
    case z.ZodIssueCode.not_finite:
      return t.the_value_must_be_finite()
    case z.ZodIssueCode.too_big:
    case z.ZodIssueCode.too_small: {
      const { exact, inclusive, type, ...rest } = issue
      const isCollection =
        type === 'array' || type === 'set' || type === 'string'
      const isMaximum = 'maximum' in rest
      const value = Number(isMaximum ? rest.maximum : rest.minimum)

      if (isCollection && !isMaximum && rest.minimum === 1) {
        return t.required()
      }

      let output = ''

      if (type === 'array') {
        output += `${t.you_must_select()} `
      } else {
        output += type === 'date' ? t.the_date() : t.the_value()
        output += ` ${isCollection ? t.must_contain() : t.must_be()} `
      }

      if (exact) {
        output += t.exactly()
      } else if (isCollection) {
        if (isMaximum) {
          output += inclusive ? t.at_most() : t.less_than()
        } else {
          output += inclusive ? t.at_least() : t.more_than()
        }
      } else {
        output += isMaximum ? t.smaller_than() : t.greater_than()

        if (inclusive) {
          output += ` ${t.or()} ${t.equal_to()}`
        }
      }

      output += type === 'date' ? ` ${new Date(value)}` : ` ${value}`

      if (type === 'array') {
        output += ` ${value === 1 ? t.value_one() : t.value_other()}`
      } else if (type === 'string') {
        output += ` ${value === 1 ? t.character_one() : t.character_other()}`
      }

      return output
    }
    default:
      return t.invalid_value()
  }
}

/**
 * Globally overrides the default Zod error messages using custom translated messages.
 *
 * @see https://github.com/colinhacks/zod/blob/master/ERROR_HANDLING.md
 */
export const translateZodErrorMap = (t: Translation['form']): z.ZodErrorMap => {
  return (issue) => ({ message: translateIssueMessage(t, issue) })
}
